/**
 * Course Writing Question Service
 * API calls for managing pre-generated writing questions (creator editing)
 */

import { apiClient as api } from './apiClient';

export interface CourseWritingQuestion {
  id: string;
  courseId: string;
  gateType: string;
  questions: any[];
  translations: Record<string, Record<string, string>> | null;
  scenarioContext: any | null;
  metadata: {
    cefrLevel?: string;
    vocabularyUsed?: string[];
    generationModel?: string;
  } | null;
  isEdited: boolean;
  generatedAt: string | null;
  lastEditedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

// Backwards-compatible alias
export type CourseQuestQuestion = CourseWritingQuestion;

/**
 * Get all pre-generated writing questions for a course
 */
export async function getCourseWritingQuestions(courseId: string): Promise<CourseWritingQuestion[]> {
  const response = await api.get<CourseWritingQuestion[]>(`/courses/${courseId}/writing-questions`);
  return response.data;
}

/**
 * Get questions for a specific gate type
 */
export async function getGateWritingQuestions(
  courseId: string,
  gateType: string,
): Promise<CourseWritingQuestion | null> {
  const response = await api.get<CourseWritingQuestion>(`/courses/${courseId}/writing-questions/${gateType}`);
  return response.data;
}

/**
 * Update questions for a gate type (creator editing)
 */
export async function updateWritingQuestions(
  courseId: string,
  gateType: string,
  data: { questions: any[]; translations?: Record<string, Record<string, string>> | null },
): Promise<CourseWritingQuestion> {
  const response = await api.patch<CourseWritingQuestion>(
    `/courses/${courseId}/writing-questions/${gateType}`,
    data,
  );
  return response.data;
}

/**
 * Regenerate questions for a gate type using AI
 */
export async function regenerateWritingQuestions(
  courseId: string,
  gateType: string,
): Promise<CourseWritingQuestion> {
  const response = await api.post<CourseWritingQuestion>(
    `/v1/courses/${courseId}/writing-questions/${gateType}/regenerate`,
  );
  return response.data;
}

// Backwards-compatible function aliases
export const getCourseQuestQuestions = getCourseWritingQuestions;
export const getGateQuestQuestions = getGateWritingQuestions;
export const updateQuestQuestions = updateWritingQuestions;
export const regenerateQuestQuestions = regenerateWritingQuestions;
